import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Save, ClipboardCheck, ListChecks, FileSignature, Siren, CalendarClock,
  ArrowRightCircle, CheckCircle2, Plus, AlertTriangle, Stethoscope,
  PlayCircle, RotateCcw, ChevronRight, MessageSquareQuote, HelpCircle, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import HotlineStatusBadge from "./HotlineStatusBadge";
import SlaBadge from "./SlaBadge";
import { DECISION_LABELS } from "./constants";
import AttachmentsPanel from "@/pages/onboarding/AttachmentsPanel";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

function Field({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      <div className="text-sm text-[#1F2937] mt-0.5">{value || "—"}</div>
    </div>
  );
}

export default function HotlineSessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  // Form state (assessment + protocol)
  const [assessment, setAssessment] = useState({});
  const [protocol, setProtocol] = useState({ protocol_code: "", protocol_name: "", questions: [], recommendation: "" });
  const [protocolCategories, setProtocolCategories] = useState([]);
  const [decisionValues, setDecisionValues] = useState([]);
  const [escTypes, setEscTypes] = useState([]);
  const [escReasons, setEscReasons] = useState([]);
  const [fuReasons, setFuReasons] = useState([]);

  // Protocol library + guided walkthrough state
  const [availableProtocols, setAvailableProtocols] = useState([]);
  const [activeProtocol, setActiveProtocol] = useState(null); // full protocol incl. nodes
  const [walkthroughPath, setWalkthroughPath] = useState([]); // [{node, answer}]
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [suggestedDecision, setSuggestedDecision] = useState(null);

  // Dialogs
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState({ value: "", notes: "", override: false, override_reason: "", override_justification: "" });
  const [escOpen, setEscOpen] = useState(false);
  const [escForm, setEscForm] = useState({ type: "", reason: "", priority: "high", comments: "", assignee_id: "" });
  const [fuOpen, setFuOpen] = useState(false);
  const [fuForm, setFuForm] = useState({ assignee_id: "", due_date: "", priority: "routine", reason: "", notes: "" });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, e, f, a] = await Promise.all([
        api.get(`/hotline/sessions/${id}`),
        api.get(`/hotline/sessions/${id}/timeline`),
        api.get(`/hotline/sessions/${id}/escalations`),
        api.get(`/hotline/sessions/${id}/follow-ups`),
        api.get(`/hotline/sessions/${id}/audit`),
      ]);
      setSession(s.data);
      setAssessment(s.data.assessment || {});
      setProtocol(s.data.protocol || { protocol_code: "", protocol_name: "", questions: [], recommendation: "" });
      setTimeline(t.data.items || []);
      setEscalations(e.data.items || []);
      setFollowups(f.data.items || []);
      setAudit(a.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    async function loadLookups() {
      try {
        const cats = await api.get("/lookups/categories", { params: { page_size: 200 } });
        const find = (code) => cats.data.items.find((c) => c.code === code);
        const fetch = async (code) => {
          const c = find(code);
          if (!c) return [];
          const r = await api.get("/lookups/values", { params: { category_id: c.id } });
          return r.data.items;
        };
        const [pc, dv, et, er, fr] = await Promise.all([
          fetch("protocol_categories"), fetch("hotline_decisions"),
          fetch("escalation_types"), fetch("escalation_reasons"), fetch("followup_reasons"),
        ]);
        setProtocolCategories(pc); setDecisionValues(dv); setEscTypes(et); setEscReasons(er); setFuReasons(fr);
        const u = await api.get("/users", { params: { page_size: 100, status: "active" } });
        setUsers(u.data.items);
      } catch (_e) {}
    }
    loadLookups();
  }, []);

  if (loading || !session) {
    return <div className="p-6 text-sm text-[#6B7280]" data-testid="hotline-detail-loading">Loading…</div>;
  }

  const isClosed = session.status === "closed";

  // --- actions ---
  async function saveAssessment() {
    setSaving(true);
    try {
      const res = await api.put(`/hotline/sessions/${id}`, { assessment });
      setSession(res.data);
      toast.success("Assessment saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  async function completeAssessment() {
    setSaving(true);
    try {
      const res = await api.post(`/hotline/sessions/${id}/complete-assessment`);
      setSession(res.data);
      toast.success("Assessment completed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  async function saveProtocol() {
    setSaving(true);
    try {
      const res = await api.post(`/hotline/sessions/${id}/execute-protocol`, protocol);
      setSession(res.data);
      toast.success("Protocol saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  function addQuestion() {
    setProtocol((p) => ({ ...p, questions: [...(p.questions || []), { id: `q${(p.questions || []).length + 1}`, text: "", answer: "" }] }));
  }
  function updateQuestion(idx, field, val) {
    setProtocol((p) => ({ ...p, questions: p.questions.map((q, i) => i === idx ? { ...q, [field]: val } : q) }));
  }
  function removeQuestion(idx) {
    setProtocol((p) => ({ ...p, questions: p.questions.filter((_, i) => i !== idx) }));
  }

  // ----- Protocol library + guided walkthrough -----
  async function loadAvailableProtocols(categoryCode) {
    if (!categoryCode) { setAvailableProtocols([]); return; }
    try {
      const r = await api.get("/protocols", { params: { category_code: categoryCode, status: "active" } });
      setAvailableProtocols(r.data.items || []);
    } catch (_e) { setAvailableProtocols([]); }
  }

  function findRoot(nodes) {
    return nodes.find((n) => !n.parent_id) || nodes[0];
  }
  function findChild(nodes, parentId, branch) {
    return nodes
      .filter((n) => n.parent_id === parentId && n.branch === branch)
      .sort((a, b) => (a.order || 0) - (b.order || 0))[0];
  }

  async function startProtocol(protocolId) {
    try {
      const r = await api.get(`/protocols/${protocolId}`);
      setActiveProtocol(r.data);
      setWalkthroughPath([]);
      setSuggestedDecision(null);
      const root = findRoot(r.data.nodes || []);
      setActiveNodeId(root?.id || null);
      setProtocol((p) => ({
        ...p,
        protocol_code: r.data.code,
        protocol_name: r.data.name,
        questions: [],
        recommendation: "",
      }));
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function answerNode(answer) {
    if (!activeProtocol) return;
    const node = activeProtocol.nodes.find((n) => n.id === activeNodeId);
    if (!node) return;
    const nextPath = [...walkthroughPath, { node, answer }];
    setWalkthroughPath(nextPath);

    // Find next node
    const next = findChild(activeProtocol.nodes, node.id, answer);
    if (next) {
      if (next.kind === "recommendation") {
        // Terminal — record and stop
        setWalkthroughPath((p) => [...p, { node: next, answer: null }]);
        setActiveNodeId(null);
        setSuggestedDecision(next.suggested_decision || null);
        setProtocol((p) => ({
          ...p,
          recommendation: next.text || "",
        }));
      } else {
        setActiveNodeId(next.id);
      }
    } else {
      // No further node — walkthrough complete
      setActiveNodeId(null);
      setSuggestedDecision(null);
    }
  }

  function resetWalkthrough() {
    if (!activeProtocol) return;
    setWalkthroughPath([]);
    setSuggestedDecision(null);
    setActiveNodeId(findRoot(activeProtocol.nodes || [])?.id || null);
  }

  function discardProtocol() {
    setActiveProtocol(null);
    setWalkthroughPath([]);
    setActiveNodeId(null);
    setSuggestedDecision(null);
  }

  async function saveWalkthrough() {
    if (!activeProtocol) return;
    const questions = walkthroughPath.map((step) => ({
      id: step.node.id,
      text: step.node.kind === "recommendation" ? `[Recommendation] ${step.node.text}` : step.node.text,
      answer: step.answer || (step.node.kind === "recommendation" ? "na" : ""),
    }));
    setSaving(true);
    try {
      const payload = {
        protocol_code: activeProtocol.code,
        protocol_name: activeProtocol.name,
        questions,
        recommendation: protocol.recommendation || "",
      };
      const res = await api.post(`/hotline/sessions/${id}/execute-protocol`, payload);
      setSession(res.data);
      toast.success("Protocol execution saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  function openDecisionWithSuggestion() {
    if (suggestedDecision) {
      setDecision((d) => ({ ...d, value: suggestedDecision, notes: protocol.recommendation || "" }));
    }
    setDecisionOpen(true);
  }
  async function captureDecision() {
    if (!decision.value) { toast.error("Pick a decision"); return; }
    if (decision.override && (!decision.override_reason || !decision.override_justification)) {
      toast.error("Override reason and clinical justification are required.");
      return;
    }
    try {
      const res = await api.post(`/hotline/sessions/${id}/capture-decision`, decision);
      setSession(res.data);
      toast.success("Decision captured");
      setDecisionOpen(false);
      load();
      if (decision.value === "refer_hah" && res.data.referral_request_id) {
        toast.success(`H@H referral created — opening onboarding draft.`);
        setTimeout(() => navigate(`/onboarding/requests/${res.data.referral_request_id}`), 1500);
      }
      if (decision.value === "teleconsultation_referral" && res.data.teleconsultation_id) {
        toast.success(`Teleconsultation initiated — opening room.`);
        setTimeout(() => navigate(`/teleconsult/room/${res.data.teleconsultation_id}`), 1500);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function createEsc() {
    if (!escForm.type || !escForm.reason) { toast.error("Type and reason are required"); return; }
    try {
      await api.post(`/hotline/sessions/${id}/escalations`, escForm);
      toast.success("Escalation created");
      setEscOpen(false);
      setEscForm({ type: "", reason: "", priority: "high", comments: "", assignee_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function createFu() {
    if (!fuForm.assignee_id || !fuForm.due_date || !fuForm.reason) { toast.error("Assignee, due date, and reason are required"); return; }
    try {
      await api.post(`/hotline/sessions/${id}/follow-ups`, fuForm);
      toast.success("Follow-up created");
      setFuOpen(false);
      setFuForm({ assignee_id: "", due_date: "", priority: "routine", reason: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function close() {
    try {
      await api.post(`/hotline/sessions/${id}/close`, { closure_notes: "Closed by nurse." });
      toast.success("Session closed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function updateEscStatus(eid, status) {
    try {
      await api.put(`/hotline/escalations/${eid}/status`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function updateFuStatus(fid, status) {
    try {
      await api.put(`/hotline/follow-ups/${fid}/status`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="hotline-detail-page">
      <PageHeader
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span>{session.record_id}</span>
            <HotlineStatusBadge status={session.status} testId="hotline-detail-status" />
            {session.sla && <SlaBadge sla={session.sla} testId="hotline-detail-sla" />}
          </span>
        }
        subtitle={`${session.patient?.first_name} ${session.patient?.last_name} · ${session.patient?.mrn} · ${session.chief_complaint?.description || ""}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {!isClosed && (
              <>
                <Button variant="outline" className="border-[#E5E7EB]" onClick={() => setEscOpen(true)} data-testid="hotline-action-escalate">
                  <Siren className="w-4 h-4 mr-1.5" /> Escalate
                </Button>
                <Button variant="outline" className="border-[#E5E7EB]" onClick={() => setFuOpen(true)} data-testid="hotline-action-followup">
                  <CalendarClock className="w-4 h-4 mr-1.5" /> Add Follow-Up
                </Button>
                <Button className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" onClick={() => setDecisionOpen(true)} data-testid="hotline-action-decision">
                  <FileSignature className="w-4 h-4 mr-1.5" /> Capture Decision
                </Button>
                <Button className="bg-[#10B981] hover:bg-[#059669] text-white" onClick={close} data-testid="hotline-action-close">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Close Session
                </Button>
              </>
            )}
          </div>
        }
      />

      {session.referral_request_id && (
        <div className="mb-4 p-4 rounded-lg bg-[#EAF8FA] border border-[#0B7E8C]/15 text-[#0B7E8C] flex items-center gap-3" data-testid="hotline-referral-banner">
          <ArrowRightCircle className="w-5 h-5" />
          <div className="flex-1">
            <div className="font-semibold">H@H Onboarding referral created</div>
            <div className="text-sm text-[#1F2937]/80">This session has spawned a downstream onboarding draft.</div>
          </div>
          <Button variant="outline" className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]" onClick={() => navigate(`/onboarding/requests/${session.referral_request_id}`)} data-testid="hotline-view-referral">
            Open referral
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F5F7FA]">
          <TabsTrigger value="overview" data-testid="hotline-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="assessment" data-testid="hotline-tab-assessment">Assessment</TabsTrigger>
          <TabsTrigger value="protocol" data-testid="hotline-tab-protocol">Protocol</TabsTrigger>
          <TabsTrigger value="decision" data-testid="hotline-tab-decision">Decision</TabsTrigger>
          <TabsTrigger value="escalations" data-testid="hotline-tab-escalations">Escalations <span className="ml-1 text-xs text-[#6B7280]">{escalations.length}</span></TabsTrigger>
          <TabsTrigger value="followups" data-testid="hotline-tab-followups">Follow-Ups <span className="ml-1 text-xs text-[#6B7280]">{followups.length}</span></TabsTrigger>
          <TabsTrigger value="timeline" data-testid="hotline-tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="attachments" data-testid="hotline-tab-attachments">Attachments</TabsTrigger>
          <TabsTrigger value="audit" data-testid="hotline-tab-audit">Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="hah-card p-5">
            <div className="hah-section-title">Session</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">Session information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field label="Record ID" value={session.record_id} />
              <Field label="Created" value={new Date(session.created_at).toLocaleString()} />
              <Field label="Nurse" value={`${session.nurse?.first_name || ""} ${session.nurse?.last_name || ""}`.trim()} />
              <Field label="Priority" value={session.priority} />
            </div>
          </div>
          <div className="hah-card p-5">
            <div className="hah-section-title">Patient</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">
              {session.patient?.first_name} {session.patient?.last_name}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field label="MRN" value={session.patient?.mrn} />
              <Field label="DOB" value={session.patient?.dob} />
              <Field label="Gender" value={session.patient?.gender} />
              <Field label="Phone" value={session.patient?.phone} />
              <Field label="Patient type" value={session.patient?.patient_type === "hotline_only" ? "Hotline-only" : "H@H"} />
              <Field label="National ID" value={session.patient?.national_id} />
              <Field label="City" value={session.patient?.city} />
            </div>
          </div>
          <div className="hah-card p-5">
            <div className="hah-section-title">Complaint</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">Chief complaint</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field label="Category" value={session.chief_complaint?.category} />
              <Field label="Severity" value={session.chief_complaint?.severity} />
              <Field label="Duration" value={session.chief_complaint?.duration} />
              <Field label="Description" value={session.chief_complaint?.description} full />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="assessment" className="mt-4">
          <FormSection title="Assessment" testId="hotline-assessment-form" description="Structured nurse-led assessment.">
            <FormField label="Symptoms" full><Textarea rows={3} value={assessment.symptoms || ""} disabled={isClosed} onChange={(e) => setAssessment({ ...assessment, symptoms: e.target.value })} data-testid="hotline-symptoms" /></FormField>
            <FormField label="Symptom duration"><Input value={assessment.symptom_duration || ""} disabled={isClosed} onChange={(e) => setAssessment({ ...assessment, symptom_duration: e.target.value })} /></FormField>
            <FormField label="Patient responses"><Input value={assessment.patient_responses || ""} disabled={isClosed} onChange={(e) => setAssessment({ ...assessment, patient_responses: e.target.value })} /></FormField>
            <FormField label="Clinical observations" full><Textarea rows={3} value={assessment.clinical_observations || ""} disabled={isClosed} onChange={(e) => setAssessment({ ...assessment, clinical_observations: e.target.value })} data-testid="hotline-observations" /></FormField>
            <FormField label="Nurse notes" full><Textarea rows={3} value={assessment.nurse_notes || ""} disabled={isClosed} onChange={(e) => setAssessment({ ...assessment, nurse_notes: e.target.value })} data-testid="hotline-nurse-notes" /></FormField>
          </FormSection>
          {!isClosed && (
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button onClick={saveAssessment} disabled={saving} variant="outline" className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]" data-testid="hotline-save-assessment">
                <Save className="w-4 h-4 mr-1.5" /> Save assessment
              </Button>
              <Button onClick={completeAssessment} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-complete-assessment">
                <ClipboardCheck className="w-4 h-4 mr-1.5" /> Complete assessment
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="protocol" className="mt-4">
          <FormSection title="Protocol Execution" testId="hotline-protocol-form" description="Pick a protocol category, choose a published clinical protocol, and run the guided decision tree.">
            <FormField label="Protocol category">
              <Select value={protocol.protocol_code ? (activeProtocol?.category_code || "") : ""} onValueChange={(v) => {
                setProtocol({ ...protocol, protocol_code: "", protocol_name: "" });
                loadAvailableProtocols(v);
              }}>
                <SelectTrigger data-testid="hotline-protocol-cat"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {protocolCategories.map((c) => <SelectItem key={c.id} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Available protocols" hint="Active templates configured by the admin team.">
              <Select
                value={activeProtocol?.id || ""}
                onValueChange={(v) => startProtocol(v)}
                disabled={availableProtocols.length === 0}
              >
                <SelectTrigger data-testid="hotline-protocol-pick">
                  <SelectValue placeholder={availableProtocols.length === 0 ? "Pick a category first" : "Select protocol"} />
                </SelectTrigger>
                <SelectContent>
                  {availableProtocols.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
          </FormSection>

          {/* Guided walkthrough */}
          {activeProtocol && (
            <div className="hah-card p-5 mt-4" data-testid="hotline-walkthrough">
              <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                <div>
                  <div className="hah-section-title">Walkthrough</div>
                  <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">{activeProtocol.name}</h3>
                  <p className="text-xs text-[#6B7280] mt-0.5">{activeProtocol.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="border-[#E5E7EB]" onClick={resetWalkthrough} data-testid="hotline-walk-reset">
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restart
                  </Button>
                  <Button size="sm" variant="outline" className="border-[#EF444433] text-[#EF4444] hover:bg-[#EF444412]" onClick={discardProtocol} data-testid="hotline-walk-discard">
                    Discard
                  </Button>
                </div>
              </div>

              {/* Path so far */}
              {walkthroughPath.length > 0 && (
                <div className="space-y-2 mb-4">
                  {walkthroughPath.map((step, idx) => {
                    const isRec = step.node.kind === "recommendation";
                    return (
                      <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${isRec ? "bg-[#EAF8FA] border border-[#0B7E8C]/20" : "bg-[#F5F7FA] border border-[#E5E7EB]"}`}>
                        <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${isRec ? "bg-[#0B7E8C] text-white" : "bg-white text-[#0B7E8C]"}`}>
                          {isRec ? <MessageSquareQuote className="w-3.5 h-3.5" /> : <HelpCircle className="w-3.5 h-3.5" />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] uppercase font-semibold text-[#6B7280] tracking-wider">
                            {isRec ? "Recommendation" : `Question ${idx + 1}`}
                          </div>
                          <div className="text-sm text-[#1F2937]">{step.node.text}</div>
                          {step.answer && (
                            <div className="text-xs mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-[#E5E7EB] font-semibold uppercase text-[#0B7E8C]">
                              <ChevronRight className="w-3 h-3" /> {step.answer}
                            </div>
                          )}
                          {isRec && step.node.suggested_decision && (
                            <div className="text-xs mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-[#0B7E8C]/30 text-[#0B7E8C]">
                              <Sparkles className="w-3 h-3" /> Suggests: {DECISION_LABELS[step.node.suggested_decision] || step.node.suggested_decision}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Active question */}
              {activeNodeId && (() => {
                const node = activeProtocol.nodes.find((n) => n.id === activeNodeId);
                if (!node) return null;
                return (
                  <div className="p-5 rounded-lg border-2 border-[#0B7E8C] bg-white" data-testid="hotline-walk-active">
                    <div className="text-[11px] uppercase font-semibold text-[#0B7E8C] tracking-wider mb-2">Current question</div>
                    <div className="text-base font-semibold text-[#1F2937] mb-4">{node.text}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button onClick={() => answerNode("yes")} disabled={isClosed} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="hotline-answer-yes">Yes</Button>
                      <Button onClick={() => answerNode("no")} disabled={isClosed} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="hotline-answer-no">No</Button>
                      <Button onClick={() => answerNode("na")} disabled={isClosed} variant="outline" className="border-[#E5E7EB]" data-testid="hotline-answer-na">N/A</Button>
                    </div>
                  </div>
                );
              })()}

              {/* Terminal — show actions */}
              {!activeNodeId && walkthroughPath.length > 0 && (
                <div className="p-4 rounded-lg bg-[#F0FDF4] border border-[#10B981]/30">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#10B981] mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold text-[#1F2937]">Walkthrough complete</div>
                      <div className="text-sm text-[#6B7280] mt-0.5">Save the protocol execution and capture the decision.</div>
                    </div>
                  </div>
                  <FormField label="Recommendation note (optional)" full>
                    <Textarea rows={2} value={protocol.recommendation || ""} onChange={(e) => setProtocol({ ...protocol, recommendation: e.target.value })} data-testid="hotline-walk-recommendation" />
                  </FormField>
                  <div className="flex items-center justify-end gap-2 mt-3">
                    <Button onClick={saveWalkthrough} disabled={isClosed || saving} variant="outline" className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]" data-testid="hotline-walk-save">
                      <Save className="w-4 h-4 mr-1.5" /> Save execution
                    </Button>
                    {suggestedDecision && (
                      <Button onClick={openDecisionWithSuggestion} disabled={isClosed} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-walk-decision">
                        <FileSignature className="w-4 h-4 mr-1.5" /> Use suggested decision
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Show saved protocol (if any) */}
          {!activeProtocol && session.protocol?.questions?.length > 0 && (
            <div className="hah-card p-5 mt-4">
              <div className="hah-section-title">Last execution</div>
              <h3 className="text-base font-semibold text-[#1F2937] mt-0.5 mb-3">{session.protocol.protocol_name}</h3>
              <div className="space-y-2">
                {session.protocol.questions.map((q, idx) => (
                  <div key={q.id || idx} className="flex items-start gap-3 p-2 rounded bg-[#F5F7FA] border border-[#E5E7EB]">
                    <span className="w-6 h-6 rounded bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">{idx + 1}</span>
                    <div className="flex-1 text-sm text-[#1F2937]">{q.text}</div>
                    {q.answer && <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-white border border-[#E5E7EB] text-[#0B7E8C]">{q.answer}</span>}
                  </div>
                ))}
              </div>
              {session.protocol.recommendation && (
                <div className="mt-3 p-3 rounded bg-[#EAF8FA] border border-[#0B7E8C]/20 text-sm text-[#1F2937]">
                  <div className="text-[11px] uppercase font-semibold text-[#0B7E8C] tracking-wider mb-1">Recommendation</div>
                  {session.protocol.recommendation}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="decision" className="mt-4">
          <div className="hah-card p-5">
            <div className="hah-section-title">Captured decision</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-3">
              {session.decision?.value ? (DECISION_LABELS[session.decision.value] || session.decision.value) : "No decision captured"}
            </h3>
            {session.decision ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <Field label="Decision" value={DECISION_LABELS[session.decision.value] || session.decision.value} />
                <Field label="Captured at" value={session.decision.decided_at ? new Date(session.decision.decided_at).toLocaleString() : "—"} />
                <Field label="Notes" full value={session.decision.notes} />
                {session.decision.override && (
                  <>
                    <Field label="Override reason" value={session.decision.override_reason} />
                    <Field label="Clinical justification" full value={session.decision.override_justification} />
                  </>
                )}
              </div>
            ) : (
              <div className="text-sm text-[#6B7280]">Click "Capture Decision" in the header to record the outcome.</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="escalations" className="mt-4">
          <div className="hah-card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-[#1F2937]">Escalations</h3>
              {!isClosed && <Button onClick={() => setEscOpen(true)} size="sm" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-add-esc"><Plus className="w-4 h-4 mr-1.5" /> Add</Button>}
            </div>
            {escalations.length === 0 ? (
              <div className="text-sm text-[#6B7280] py-4">No escalations.</div>
            ) : (
              <div className="space-y-2">
                {escalations.map((e) => (
                  <div key={e.id} className="p-3 border border-[#E5E7EB] rounded-lg">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-medium text-[#1F2937] capitalize">{e.type.replace(/_/g, " ")}</span>
                      <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded" style={{ background: "#EF444414", color: "#EF4444" }}>{e.priority}</span>
                      <span className="text-xs text-[#6B7280] capitalize">{e.status.replace(/_/g, " ")}</span>
                      <span className="text-xs text-[#94A3B8] ml-auto">{new Date(e.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-[#1F2937] mt-1">{e.reason}</div>
                    {e.comments && <div className="text-xs text-[#6B7280] mt-1">{e.comments}</div>}
                    <div className="flex items-center gap-2 mt-2">
                      {["open", "in_progress", "completed", "cancelled"].filter((s) => s !== e.status).map((s) => (
                        <Button key={s} size="sm" variant="outline" className="border-[#E5E7EB] text-xs h-7" onClick={() => updateEscStatus(e.id, s)} data-testid={`hotline-esc-${e.id}-${s}`}>
                          Mark {s.replace(/_/g, " ")}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="followups" className="mt-4">
          <div className="hah-card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-[#1F2937]">Follow-Ups</h3>
              {!isClosed && <Button onClick={() => setFuOpen(true)} size="sm" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-add-fu"><Plus className="w-4 h-4 mr-1.5" /> Add</Button>}
            </div>
            {followups.length === 0 ? (
              <div className="text-sm text-[#6B7280] py-4">No follow-ups.</div>
            ) : (
              <div className="space-y-2">
                {followups.map((f) => (
                  <div key={f.id} className="p-3 border border-[#E5E7EB] rounded-lg">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-medium text-[#1F2937]">Due {f.due_date}</span>
                      <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-[#0B7E8C14] text-[#0B7E8C]">{f.priority}</span>
                      <span className="text-xs text-[#6B7280] capitalize">{f.status.replace(/_/g, " ")}</span>
                      <span className="text-xs text-[#94A3B8] ml-auto">Assignee: {users.find((u) => u.id === f.assignee_id)?.email || f.assignee_id}</span>
                    </div>
                    <div className="text-sm text-[#1F2937] mt-1">{f.reason}</div>
                    {f.notes && <div className="text-xs text-[#6B7280] mt-1">{f.notes}</div>}
                    <div className="flex items-center gap-2 mt-2">
                      {["pending", "in_progress", "completed", "cancelled"].filter((s) => s !== f.status).map((s) => (
                        <Button key={s} size="sm" variant="outline" className="border-[#E5E7EB] text-xs h-7" onClick={() => updateFuStatus(f.id, s)} data-testid={`hotline-fu-${f.id}-${s}`}>
                          Mark {s.replace(/_/g, " ")}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <div className="hah-card p-5" data-testid="hotline-timeline">
            <ol className="relative border-l border-[#E5E7EB] ml-3 space-y-5">
              {timeline.length === 0 && <li className="ml-4 text-sm text-[#6B7280]">No events yet.</li>}
              {timeline.map((e) => (
                <li key={e.id} className="ml-4 relative">
                  <span className="absolute -left-[24px] top-1 w-3 h-3 rounded-full bg-[#0B7E8C] ring-4 ring-white" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#1F2937]">{e.title}</span>
                    <span className="text-xs text-[#6B7280]">{new Date(e.created_at).toLocaleString()}</span>
                  </div>
                  {e.description && <div className="text-sm text-[#6B7280] mt-1">{e.description}</div>}
                  <div className="text-xs text-[#94A3B8] mt-1">{e.actor_name || e.actor_email || "system"} · {(e.actor_role || "").replace(/_/g, " ")}</div>
                </li>
              ))}
            </ol>
          </div>
        </TabsContent>

        <TabsContent value="attachments" className="mt-4">
          <AttachmentsPanel requestId={id} title="Session attachments" defaultCategory="other" />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="hah-card p-5">
            <h3 className="text-base font-semibold text-[#1F2937] mb-3">Audit history</h3>
            {audit.length === 0 ? (
              <div className="text-sm text-[#6B7280]">No audit events.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F5F7FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Action</th>
                    <th className="text-left px-3 py-2">By</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-t border-[#E5E7EB]">
                      <td className="px-3 py-2 text-xs text-[#6B7280]">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 text-[#1F2937]">{a.action}</td>
                      <td className="px-3 py-2 text-[#6B7280]">{a.user_email || "system"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Decision dialog */}
      <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
        <DialogContent data-testid="hotline-decision-dialog">
          <DialogHeader>
            <DialogTitle>Capture decision</DialogTitle>
            <DialogDescription>Record the clinical decision for this hotline call.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Decision <span className="text-[#EF4444]">*</span></label>
              <Select value={decision.value} onValueChange={(v) => setDecision({ ...decision, value: v })}>
                <SelectTrigger data-testid="hotline-decision-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {decisionValues.length > 0 ? decisionValues.map((d) => (
                    <SelectItem key={d.id} value={d.value}>{d.label}</SelectItem>
                  )) : Object.entries(DECISION_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Notes</label>
              <Textarea rows={3} value={decision.notes} onChange={(e) => setDecision({ ...decision, notes: e.target.value })} data-testid="hotline-decision-notes" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={decision.override} onCheckedChange={(v) => setDecision({ ...decision, override: !!v })} data-testid="hotline-decision-override" />
              Override protocol recommendation
            </label>
            {decision.override && (
              <>
                <div>
                  <label className="text-sm font-medium text-[#1F2937]">Override reason <span className="text-[#EF4444]">*</span></label>
                  <Input value={decision.override_reason} onChange={(e) => setDecision({ ...decision, override_reason: e.target.value })} data-testid="hotline-override-reason" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1F2937]">Clinical justification <span className="text-[#EF4444]">*</span></label>
                  <Textarea rows={2} value={decision.override_justification} onChange={(e) => setDecision({ ...decision, override_justification: e.target.value })} data-testid="hotline-override-justification" />
                </div>
              </>
            )}
            {decision.value === "refer_hah" && (
              <div className="p-3 rounded-lg bg-[#EAF8FA] border border-[#0B7E8C]/15 text-sm text-[#0B7E8C]">
                <strong>Heads up:</strong> Capturing "Refer to H@H Program" will automatically create an Onboarding draft request and notify the hospital coordinator.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={captureDecision} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-decision-save">
              <FileSignature className="w-4 h-4 mr-1.5" /> Capture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalation dialog */}
      <Dialog open={escOpen} onOpenChange={setEscOpen}>
        <DialogContent data-testid="hotline-esc-dialog">
          <DialogHeader>
            <DialogTitle>Create escalation</DialogTitle>
            <DialogDescription>Route this case to the appropriate team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Type <span className="text-[#EF4444]">*</span></label>
              <Select value={escForm.type} onValueChange={(v) => setEscForm({ ...escForm, type: v })}>
                <SelectTrigger data-testid="hotline-esc-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {escTypes.map((e) => <SelectItem key={e.id} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Reason <span className="text-[#EF4444]">*</span></label>
              <Select value={escForm.reason} onValueChange={(v) => setEscForm({ ...escForm, reason: v })}>
                <SelectTrigger data-testid="hotline-esc-reason"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {escReasons.map((e) => <SelectItem key={e.id} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Priority</label>
              <Select value={escForm.priority} onValueChange={(v) => setEscForm({ ...escForm, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Assignee</label>
              <Select value={escForm.assignee_id} onValueChange={(v) => setEscForm({ ...escForm, assignee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Pick user (optional)" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} — {u.role_code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Comments</label>
              <Textarea rows={2} value={escForm.comments} onChange={(e) => setEscForm({ ...escForm, comments: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEscOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={createEsc} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="hotline-esc-save">
              <Siren className="w-4 h-4 mr-1.5" /> Create escalation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Follow-up dialog */}
      <Dialog open={fuOpen} onOpenChange={setFuOpen}>
        <DialogContent data-testid="hotline-fu-dialog">
          <DialogHeader>
            <DialogTitle>Create follow-up</DialogTitle>
            <DialogDescription>Schedule a follow-up task for this patient.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Assignee <span className="text-[#EF4444]">*</span></label>
              <Select value={fuForm.assignee_id} onValueChange={(v) => setFuForm({ ...fuForm, assignee_id: v })}>
                <SelectTrigger data-testid="hotline-fu-assignee"><SelectValue placeholder="Pick user" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} — {u.role_code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Due date <span className="text-[#EF4444]">*</span></label>
              <Input type="date" value={fuForm.due_date} onChange={(e) => setFuForm({ ...fuForm, due_date: e.target.value })} data-testid="hotline-fu-date" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Priority</label>
              <Select value={fuForm.priority} onValueChange={(v) => setFuForm({ ...fuForm, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="stat">STAT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Reason <span className="text-[#EF4444]">*</span></label>
              <Select value={fuForm.reason} onValueChange={(v) => setFuForm({ ...fuForm, reason: v })}>
                <SelectTrigger data-testid="hotline-fu-reason"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {fuReasons.map((r) => <SelectItem key={r.id} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Notes</label>
              <Textarea rows={2} value={fuForm.notes} onChange={(e) => setFuForm({ ...fuForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFuOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={createFu} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-fu-save">
              <CalendarClock className="w-4 h-4 mr-1.5" /> Create follow-up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
